import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleCallback } from "@/components/auth/GoogleCallback";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n/I18nProvider";
import Layout from "@/components/app/Layout";
import { Login, Register, ForgotPassword, ResetPassword, AcceptInvite } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Organisation from "@/pages/Organisation";
import Applicability from "@/pages/Applicability";
import Assessments, { AssessmentWorkspace } from "@/pages/Assessments";
import CompareCycles from "@/pages/CompareCycles";
import { DataInventory, ProcessingActivities, Vendors, ConsentRegister, RightsRequests, BreachRegister } from "@/pages/Registers";
import Evidence from "@/pages/Evidence";
import DataSources from "@/pages/DataSources";
import DataSourceDetail from "@/pages/DataSourceDetail";
import DataProtection from "@/pages/DataProtection";
import ProcessingInventory from "@/pages/ProcessingInventory";
import Governance from "@/pages/Governance";
import OrgConsent from "@/pages/OrgConsent";
import LegalDoc from "@/pages/LegalDoc";
import Findings from "@/pages/Findings";
import Remediation from "@/pages/Remediation";
import Reports from "@/pages/Reports";
import Regulatory from "@/pages/Regulatory";
import Settings from "@/pages/Settings";

function Loading() {
  return <div className="grid h-screen place-items-center text-sm text-slate-400" data-testid="app-loading">Loading…</div>;
}

function Protected({ children, allowOnboarding, allowConsent }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" && user.tenant && !user.tenant.consent_ok && !allowConsent) return <Navigate to="/consent" replace />;
  if (!allowOnboarding && !user.tenant?.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Navigate to="/" replace /> : children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) { return <GoogleCallback />; }
  return (
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/consent" element={<Protected allowOnboarding allowConsent><OrgConsent /></Protected>} />
          <Route path="/legal/:docKey" element={<LegalDoc />} />
          <Route path="/onboarding" element={<Protected allowOnboarding><Onboarding /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="organisation" element={<Organisation />} />
            <Route path="applicability" element={<Applicability />} />
            <Route path="assessments" element={<Assessments />} />
            <Route path="assessments/compare" element={<CompareCycles />} />
            <Route path="assessments/:id" element={<AssessmentWorkspace />} />
            <Route path="inventory" element={<DataInventory />} />
            <Route path="processing" element={<ProcessingInventory />} />
            <Route path="processing-register" element={<ProcessingActivities />} />
            <Route path="governance" element={<Governance />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="consents" element={<ConsentRegister />} />
            <Route path="rights-requests" element={<RightsRequests />} />
            <Route path="breaches" element={<BreachRegister />} />
            <Route path="data-sources" element={<DataSources />} />
            <Route path="data-sources/:id" element={<DataSourceDetail />} />
            <Route path="data-protection" element={<DataProtection />} />
            <Route path="policies" element={<Evidence policies />} />
            <Route path="evidence" element={<Evidence />} />
            <Route path="findings" element={<Findings />} />
            <Route path="remediation" element={<Remediation />} />
            <Route path="reports" element={<Reports />} />
            <Route path="regulatory" element={<Regulatory />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
    </I18nProvider>
  );
}
